import { useCallback, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { OverflowMenu } from '@stitch-fix/mode-react';
import FeedbackModal from '../../FeedbackModal';
import { CancelReturnModal } from '../../CancelReturnModal/CancelReturnModal';
import {
  useWardrobeGotoUpdateMutation,
  WardrobeGotoUpdateMutation,
} from '../../../utils/hooks/updateTopItem';
import { useToast } from '../../../utils/contexts/toast-provider';
import { COPY } from '../../../utils/copy';
import { useBoughtItemsMachine } from '../../../utils/machines/boughtItems/context-provider';
import {
  GetTopItemsDocument,
  KeptItemFragment,
} from '../../../utils/hooks/getTopItems';

type TopItemsOverflowMenuProps = {
  keptItem: KeptItemFragment;
};

const leaveFeedbackOption = { id: 'leave-feedback', label: 'Leave feedback' };
const removeTopItemOption = {
  id: 'remove-top-item',
  label: 'Remove from top items',
  variant: 'destructive',
};
const cancelReturn = {
  id: 'cancel-return',
  label: 'Cancel return',
  variant: 'destructive',
};
const overflowMenuItems = (returnInProgress: boolean) => {
  return returnInProgress
    ? [cancelReturn, removeTopItemOption]
    : [leaveFeedbackOption, removeTopItemOption];
};

/*
 * The `TopItemsOverflowMenu` component handles rendering a list of actions
 * that a user can choose from a temporary popup overlay.
 */
export const TopItemsOverflowMenu = ({
  keptItem,
}: TopItemsOverflowMenuProps) => {
  const [feedbackModalOpened, setFeedbackModalOpened] =
    useState<boolean>(false);
  const [cancelReturnModalOpened, setCancelReturnModalOpened] =
    useState<boolean>(false);

  const { send } = useBoughtItemsMachine();
  const { addToast } = useToast();
  const { errorToastMessage } = COPY;

  const handleFeedbackModalClose = () => {
    setFeedbackModalOpened(false);
  };

  const handleCancelReturnModalClose = () => {
    setCancelReturnModalOpened(false);
  };

  const [removeTopItem] = useWardrobeGotoUpdateMutation({
    variables: { skuId: keptItem.sku },
    onCompleted: ({ wardrobeGotoUpdate }: WardrobeGotoUpdateMutation) => {
      const result = wardrobeGotoUpdate?.result;

      if (result && result.__typename === 'WardrobeGotoUpdateSuccess') {
        addToast({
          message: result.successMessage,
          status: 'success',
        });
      }

      send('REMOVE_TOP_ITEM', { removedSkuId: keptItem.sku });
    },
    onError: e => {
      addToast({ message: errorToastMessage });
      datadogRum.addError(e, {
        query: 'useWardrobeGotoUpdateMutation',
        component: 'TopItemsOverflowMenu',
      });
    },
    refetchQueries: [
      {
        query: GetTopItemsDocument,
      },
    ],
  });

  const handleRemoveTopItem = useCallback(() => {
    removeTopItem();
  }, [removeTopItem]);

  const handleSelect = useCallback(
    (selection: string) => {
      switch (selection) {
        case 'leave-feedback':
          return setFeedbackModalOpened(true);
        case 'remove-top-item':
          return handleRemoveTopItem();
        case 'cancel-return':
          return setCancelReturnModalOpened(true);

        default:
          return null;
      }
    },
    [handleRemoveTopItem],
  );

  return (
    <>
      <OverflowMenu
        aria-label="Top items actions"
        items={overflowMenuItems(keptItem.returnInProgress)}
        onSelect={handleSelect}
        iconButtonVariant="overlay"
      />
      <FeedbackModal
        open={feedbackModalOpened}
        handleModalClose={handleFeedbackModalClose}
        skuId={keptItem.sku}
        orderId={keptItem.orderId}
      />
      <CancelReturnModal
        open={cancelReturnModalOpened}
        handleModalClose={handleCancelReturnModalClose}
        skuId={keptItem.sku}
        orderId={keptItem.orderId}
      />
    </>
  );
};
